
import type { Error as PlayError } from '@empathybroker/tsclient-play/dist/models/error'
import type { BrowseConfigurationsConfig } from '@empathybroker/tsclient-play/dist/models/instance/domains/tools/browse-configurations-config'
import type { ConfigurationAttributeConfig } from '@empathybroker/tsclient-play/dist/models/instance/domains/tools/configuration-attribute-config'
import type { PlayResponse } from '@empathybroker/tsclient-play/dist/models/play-response'
import type { FieldValue, RegularFilter } from '../../models'
import { DomainEnum } from '@empathybroker/tsclient-play/dist/enums/domain.enum'

import { defineComponent } from 'vue'

import { mapActions } from 'vuex'

import ConfigurationsForm from '../../components/configurations/configurations-form.component.vue'
import ConfigurationsSearchInput from '../../components/configurations/configurations-search-input.component.vue'
import { UiCheckbox } from '../../components/keen-ui'

import { useFieldValues } from '../../composables/use-field-values'

import { ConfigurationToolIdEnum, FeatureIdEnum } from '../../enums'

import { CrudFormMixin } from '../../mixins'
import { ConfigurationsFormMixin } from '../../mixins/configurations-form.mixin'

import { ROUTER_CONSTS } from '../../router/router.consts'

import { SEARCH_CONSTS } from '../../store/search/search.consts'

import { ArrayUtils } from '../../utils/array.utils'

export default defineComponent({
  name: 'BrowseConfigurationsForm',
  components: {
    ConfigurationsForm,
    UiCheckbox,
    ConfigurationsSearchInput,
  },
  mixins: [ConfigurationsFormMixin],
  data() {
    return {
      updateMode:
        this.$route.name ===
        ROUTER_CONSTS.routes[FeatureIdEnum.CONFIGURATION][DomainEnum.BROWSE].update,
      item: {
        enabled: true,
        name: undefined,
        context: {
          attributeName: undefined,
          attributeValues: [],
          pivots: {},
          inheritance: false,
        },
        ranking: {
          products: {
            elements: [],
          },
          attributes: {
            elements: [],
          },
        },
        facets: {
          elements: [],
        },
        restricted: {},
      },
      query: undefined,
      ConfigurationToolIdEnum,
    }
  },
  computed: {
    toolConfig(): BrowseConfigurationsConfig {
      const toolConfig = CrudFormMixin.computed.toolConfig.bind(this)()
      if (!toolConfig.attributes) {
        return {
          attributes: [],
          useSearchConfigurations: false,
        }
      }
      return toolConfig
    },
    attributeName(): string {
      return this.$route.params[ROUTER_CONSTS.params.attribute.name]
    },
    attributeConfig(): ConfigurationAttributeConfig {
      return this.toolConfig.attributes.find(
        (attributeConfig: ConfigurationAttributeConfig) =>
          attributeConfig.id === this.attributeName,
      )
    },
    attribute(): FieldValue {
      const { fieldValues } = useFieldValues({ reset: false })
      const attributeValue = this.$route.params[ROUTER_CONSTS.params.attribute.value]
      return ArrayUtils.findInHierarchy(
        attributeValue,
        fieldValues.value[this.attributeName] ?? [],
      ).pop()
    },
    loadSearchDataEnabled(): boolean {
      return (
        ConfigurationsFormMixin.computed.loadSearchDataEnabled.bind(this)() &&
        !!this.item.context.attributeName &&
        !!this.item.context.attributeValues.length
      )
    },
    loadPreviewDisabled(): boolean {
      return !!this.query
    },
  },
  methods: {
    ...mapActions(SEARCH_CONSTS.moduleName, {
      browse: SEARCH_CONSTS.actions.browse,
      search: SEARCH_CONSTS.actions.search,
    }),
    async onItemLoad(): Promise<void> {
      Object.values(this.pivots).forEach((pivot: RegularFilter) => {
        if (!pivot.selectedValues.length) {
          const contextSelectedValues = this.context.filters[pivot.config.id]?.selectedValues
          if (contextSelectedValues?.length) {
            this.onFilterChange(pivot, contextSelectedValues)
          }
        }
      })
      if (!this.item.context.attributeName) {
        this.item.context.attributeName = this.attributeName
      }
      if (!this.item.context.attributeValues?.length) {
        this.item.context.attributeValues = [
          {
            id: this.attribute.id,
            name: this.attribute.name,
          },
        ]
      }
      await ConfigurationsFormMixin.methods.onItemLoad.bind(this)()
    },
    fetchSearchData(): Promise<PlayResponse | PlayError> {
      if (this.query) {
        const attributeFacetId = this.attributeConfig?.facetId || this.attributeConfig?.id
        return this.search({
          query: this.query,
          filters: this.searchFilters,
          pagination: this.pagination,
          params: {
            filter: `${attributeFacetId}:${this.attribute.id}`,
          },
        })
      }
      const attributeBrowseId = this.attributeConfig?.browseId || this.attributeConfig?.id
      return this.browse({
        attribute: {
          id: attributeBrowseId,
          value: this.attribute.id,
        },
        filters: this.searchFilters,
        pagination: this.pagination,
        params: {
          ...this.resultsPreviewParams,
          ...this.facetsPreviewParams,
        },
      })
    },
    async onQueryChange(query: string): Promise<void> {
      this.query = query
      await this.loadSearchData({ resetPagination: true })
    },
  },
})
